<template>
  <div class="credit-ladder">
    <!-- La escalera: una columna por TRAMO, ordenadas de menor a mayor cupo.
         El producto es una escalera, así que la vista la dibuja literalmente. -->
    <!-- Roving tabindex + flechas: el patrón tablist de ARIA exige navegar
         entre tabs con el teclado, no solo tabearlos. El Enter/Espacio sobre
         el botón burbujea al @click del envoltorio. -->
    <div class="credit-ladder__chart" role="tablist" :aria-label="$t('credits.ladder_aria')"
         @keydown.left.prevent="moverSeleccion(-1)" @keydown.right.prevent="moverSeleccion(1)">
      <!-- Una columna = envoltorio + botón. El avatar va en el ENVOLTORIO, no
           dentro del botón: el mask que talla la cuenca recorta también a los
           hijos, y ahí el avatar caía justo en la zona transparente. -->
      <div v-for="(step, idx) in steps" :key="step.tier_level"
           class="credit-ladder__col" :style="{ height: step.height_pct + '%' }"
           @click="elegir(idx)">
        <button type="button" role="tab"
                class="credit-ladder__step"
                :class="{
                  'credit-ladder__step--active': idx === seleccion,
                  'credit-ladder__step--locked': step.locked
                }"
                :id="`credit-tab-${idPrefix}${step.tier_level}`"
                :aria-controls="`credit-panel-${idPrefix}${step.tier_level}`"
                :aria-selected="idx === seleccion"
                :tabindex="idx === seleccion ? 0 : -1"
                :aria-label="`${step.tier_name} · ${step.amount}`">
          <span class="credit-ladder__step-amount"><credit-money-text :text="step.amount_short" abbreviated /></span>
          <span class="credit-ladder__step-tier">{{ step.tier_name }}</span>
        </button>
        <img :src="step.rank_avatar_url" alt="" class="credit-ladder__step-avatar"
             :class="{ 'credit-ladder__step-avatar--locked': step.locked }">
      </div>
    </div>

    <!-- Los CONTENEDORES del crédito: un carrusel con snap — una tarjeta por
         tramo, scroll horizontal sincronizado con la escalera en ambos
         sentidos (scrollear cambia la selección; elegir columna scrollea). -->
    <div v-if="steps.length" ref="cartas" class="credit-ladder__cards hide-scrollbar"
         @scroll.passive="alScrollearCartas">
      <!-- Un tabpanel por peldaño. `inert` en los que no se ven: sin él, sus
           píldoras de plazo y su CTA "Tomar crédito" seguían en el orden de
           tabulación (todas las tarjetas viven en el DOM desde el carrusel),
           y al tabear a uno oculto el navegador scrolleaba el marco para
           revelarlo, cambiando la selección de rebote. -->
      <div v-for="(carta, cidx) in steps" :key="'carta-' + carta.tier_level"
           class="credit-ladder__detail" role="tabpanel"
           :id="`credit-panel-${idPrefix}${carta.tier_level}`"
           :aria-labelledby="`credit-tab-${idPrefix}${carta.tier_level}`"
           :inert="cidx !== seleccion"
           :class="{ 'credit-ladder__detail--active': cidx === seleccion }">
        <div class="credit-ladder__art hidden md:flex">
          <img :src="carta.coins_url" class="credit-ladder__coins" alt="" />
        </div>

        <div class="credit-ladder__body">
        <div class="credit-ladder__detail-head">
          <div class="credit-card__head-body">
            <div class="credit-card__tier">{{ carta.tier_name }}</div>
            <div class="credit-card__amount-row">
              <span class="credit-card__amount"><credit-money-text :text="ofertaDe(cidx).amount" /></span>
              <img :src="starsUrl" alt="" class="credit-ladder__stars" />
            </div>
            <!-- La cuota, como dato bajo el monto: salió de la píldora verde
                 para que el único elemento destacado sea el botón. -->
            <div v-if="ofertaDe(cidx).periodic_total" class="credit-card__installment">
              {{ ofertaDe(cidx).payment_label }}: <b><credit-money-text :text="ofertaDe(cidx).periodic_total" /></b>
            </div>
          </div>
          <span v-if="ofertaDe(cidx).state_label" class="credit-ladder__chip">{{ ofertaDe(cidx).state_label }}</span>
        </div>

        <!-- El plazo conserva unidad y valor en una sola lista de opciones.
             La cuota mostrada incluye la administración correspondiente. -->
        <div class="credit-ladder__terms hide-scrollbar">
          <button v-for="o in carta.offers" :key="clavePlazo(o)" type="button"
                  class="credit-ladder__term"
                  :class="{ 'credit-ladder__term--active': clavePlazo(o) === plazoDe(cidx) }"
                  :aria-pressed="clavePlazo(o) === plazoDe(cidx)"
                  :disabled="carta.offers.length === 1"
                  @click="elegirPlazo(cidx, o)">
            {{ o.term_label }}
          </button>
        </div>

        <!-- Dos capas: el <a>/<button> exterior mantiene su caja FIJA (el click
             es estable para Playwright y para cualquier automatización) y el
             span interior lleva la píldora visual con la respiración y el
             destello. El click en el hijo burbujea al padre. -->
        <a v-if="!ofertaDe(cidx).locked" class="credit-card__cta" :href="ctaHref(ofertaDe(cidx))">
          <span class="credit-card__cta-nucleo">{{ $t('credits.take_credit') }}</span>
        </a>
        <button v-else type="button" class="credit-card__cta" @click="abrirCondiciones(ofertaDe(cidx))">
          <span class="credit-card__cta-nucleo">{{ $t('credits.take_credit') }}</span>
        </button>
        </div>
      </div>
    </div>

    <!-- Puntitos: uno por contenedor de crédito, DEBAJO de los contenedores.
         aria-hidden: duplican la función del tablist (que ya navega con
         flechas) — sirven al puntero. -->
    <div v-if="steps.length > 1" class="credit-ladder__dots" aria-hidden="true">
      <button v-for="(step, idx) in steps" :key="'dot-' + step.tier_level" type="button"
              class="credit-ladder__dot"
              :class="{ 'credit-ladder__dot--active': idx === seleccion }"
              tabindex="-1"
              @click="irA(idx)"></button>
    </div>
  </div>
</template>

<script>
import CreditMoneyText from './credit-money-text.vue';
// La escalera de tramos como columnas crecientes: una columna por cupo, de
// menor a mayor. Es la ÚNICA presentación de las ofertas (reemplazó al
// carrusel de tarjetas el 2026-08-27).
export default {
  name: 'CreditOffersLadder',
  components: { CreditMoneyText },
  props: {
    offers: { type: Array, required: true },
    viewState: { type: String, required: true },
    creditCurrency: { type: String, default: '' },
    // Prefijo de ids cuando la vista trae una escalera por moneda.
    idScope: { type: String, default: '' },
    newCreditBase: { type: String, required: true }
  },
  data () {
    return { seleccion: 0, plazo: null }
  },
  computed: {
    // Un peldaño por rango, con todos sus plazos diarios y mensuales. El
    // cupo del peldaño es estable; la tarjeta usa la oferta elegida completa.
    steps () {
      const porTramo = new Map()
      this.offers.forEach((o) => {
        const nivel = o.tier_level
        if (!porTramo.has(nivel)) porTramo.set(nivel, [])
        porTramo.get(nivel).push(o)
      })
      return Array.from(porTramo.values()).map((ofertas) => {
        ofertas.sort((a, b) => {
          const unidadA = a.term_unit === 'days' ? 0 : 1
          const unidadB = b.term_unit === 'days' ? 0 : 1
          return unidadA - unidadB || a.term - b.term
        })
        const mayorCupo = ofertas.reduce((mayor, o) => o.amount_cents > mayor.amount_cents ? o : mayor)
        return { ...mayorCupo, locked: ofertas.every((o) => o.locked), offers: ofertas }
      }).sort((a, b) => a.tier_level - b.tier_level)
    },
    idPrefix () {
      return this.idScope ? `${this.idScope}-` : ''
    },
    starsUrl () {
      // Único asset resuelto en el cliente: es estático, no depende de datos.
      return new URL('../../../../assets/images/estrellas.svg', import.meta.url).href
    }
  },
  created () {
    // Abre en el tramo TOMABLE más alto: el usuario aterriza en lo que puede
    // hacer hoy, con la escalera completa a la vista.
    const tomables = this.steps.filter((s) => !s.locked)
    if (tomables.length) {
      this.seleccion = this.steps.indexOf(tomables[tomables.length - 1])
    }
  },
  mounted () {
    // El carrusel aterriza en la tarjeta de la selección inicial, sin
    // animación: el snap instantáneo no deja ventana de inestabilidad.
    if (this.seleccion > 0) this.sincronizarCartas('auto')
    // La escalera de otra moneda nace oculta y mide cero: se alinea cuando
    // el selector la muestra.
    const cartas = this.$refs.cartas
    if (!cartas || typeof ResizeObserver !== 'function') return
    let visible = cartas.clientWidth > 0
    this._observador = new ResizeObserver(() => {
      const ahora = cartas.clientWidth > 0
      if (ahora && !visible && this.seleccion > 0) this.sincronizarCartas('auto')
      visible = ahora
    })
    this._observador.observe(cartas)
  },
  beforeUnmount () {
    if (this._observador) this._observador.disconnect()
  },
  methods: {
    elegir (idx) {
      this.seleccion = idx
      this.plazo = null
      this.sincronizarCartas('smooth')
    },
    clavePlazo (offer) {
      return `${offer.term_unit}:${offer.term}`
    },
    ofertaInicial (paso) {
      return paso.offers.find((o) => !o.locked) || paso.offers[0]
    },
    // Unidad y valor identifican el plazo: no se pierde la selección cuando
    // se intercalan ofertas y un valor diario no colisiona con uno mensual.
    ofertaDe (cidx) {
      const paso = this.steps[cidx]
      return paso.offers.find((o) => this.clavePlazo(o) === this.plazoDe(cidx)) || this.ofertaInicial(paso)
    },
    plazoDe (cidx) {
      return cidx === this.seleccion && this.plazo
        ? this.plazo
        : this.clavePlazo(this.ofertaInicial(this.steps[cidx]))
    },
    elegirPlazo (cidx, offer) {
      this.seleccion = cidx
      this.plazo = this.clavePlazo(offer)
    },
    // Scroll del carrusel → selección: la tarjeta más cercana manda. Sin
    // llamar a elegir(), que scrollearía de vuelta (lazo de feedback).
    alScrollearCartas () {
      // El scroll disparado por sincronizarCartas no debe re-decidir la
      // selección a mitad de vuelo (parpadeo de tarjetas y plazo).
      if (this._sincronizando) return
      const el = this.$refs.cartas
      if (!el || !el.clientWidth) return
      // Geometría real, no aritmética de índices: la tarjeta cuyo centro
      // quede más cerca del centro del marco es la seleccionada — inmune a
      // anchos fraccionarios y a slides más angostos que el marco.
      // Todo en coordenadas de VIEWPORT. offsetLeft mide desde el
      // offsetParent, que NO es el marco (es static, así que el ancestro
      // posicionado resultó ser .arcadi-card): medido, offsetLeft 68 donde la
      // posición real dentro del marco es 6. Hoy ese sesgo de 62px no se ve
      // —es igual para todas las tarjetas y el scroll-snap corrige el
      // destino—, pero mezclar dos orígenes de coordenadas se rompe solo
      // cuando alguien posicione un ancestro o cambie el layout.
      const marco = el.getBoundingClientRect()
      const centro = marco.left + marco.width / 2
      let idx = 0
      let mejor = Infinity
      Array.from(el.children).forEach((hijo, i) => {
        const r = hijo.getBoundingClientRect()
        const d = Math.abs(r.left + r.width / 2 - centro)
        if (d < mejor) { mejor = d; idx = i }
      })
      if (idx !== this.seleccion && this.steps[idx]) {
        this.seleccion = idx
        this.plazo = null
      }
    },
    sincronizarCartas (comportamiento) {
      this._sincronizando = true
      clearTimeout(this._syncTimer)
      this._syncTimer = setTimeout(() => { this._sincronizando = false }, 600)
      this.$nextTick(() => {
        const el = this.$refs.cartas
        const hijo = el && el.children[this.seleccion]
        if (!hijo) return
        // Desplazamiento RELATIVO: cuánto hay que correr el marco para que
        // el centro de la tarjeta caiga en el centro del marco (espejo del
        // snap-align center del CSS). Relativo y no absoluto porque
        // offsetLeft no mide desde el marco — ver alScrollearCartas.
        const marco = el.getBoundingClientRect()
        const r = hijo.getBoundingClientRect()
        const delta = (r.left + r.width / 2) - (marco.left + marco.width / 2)
        el.scrollTo({ left: el.scrollLeft + delta, behavior: comportamiento })
      })
    },
    irA (idx) {
      this.elegir(idx)
      this.$nextTick(() => {
        const col = this.$el.querySelectorAll('.credit-ladder__col')[idx]
        if (col) col.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
      })
    },
    moverSeleccion (delta) {
      const n = this.steps.length
      if (!n) return
      this.elegir((this.seleccion + delta + n) % n)
      this.$nextTick(() => {
        const tabs = this.$el.querySelectorAll('[role="tab"]')
        if (tabs[this.seleccion]) tabs[this.seleccion].focus()
      })
    },
    ctaHref (offer) {
      if (this.viewState !== 'eligible') return '#'
      const params = new URLSearchParams({ amount: offer.amount_cents, term: offer.term, term_unit: offer.term_unit })
      if (this.creditCurrency) params.set('currency', this.creditCurrency)
      return `${this.newCreditBase}?${params}`
    },
    abrirCondiciones (offer) {
      const toggleId = offer.lock_toggle_id || `credits-lock-toggle-${offer.tier_level}`
      const toggle = document.getElementById(toggleId)
      if (!toggle) return
      toggle.checked = true
      // Marcar checked por código no dispara `change`, y el manejo de foco
      // del modal (credits-new-recalc.js) vive en ese evento.
      toggle.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }
}
</script>
